import { createAvatar } from '@dicebear/core';
import { bottts } from '@dicebear/collection';

// Robot avatars for agent accounts (Settings → Users & Groups → Agents).
// Generated locally (no network request — @dicebear/collection ships the
// SVG parts, nothing is fetched from an external avatar service), seeded so
// the same agent always gets the same-looking robot, and the same owner
// always gets the same color — not truly random on every render, but reads
// as "many different robots" since `bottts` combines eyes/face/mouth/sides/
// texture/top independently (far more than 10 distinct results).

// A fixed, deliberately varied palette (not the app's `brand` teal alone —
// the point is owners are visually distinguishable from each other) —
// avoids red/green, which this app already uses for destructive/success
// states elsewhere, so a robot's color is never mistaken for a status.
const OWNER_COLORS = [
  '5bc4d8', // brand teal
  '7c6fe0', // violet
  'e0a23c', // amber
  'e07cae', // pink
  '6fae5a', // sage (muted, not success-green)
  '4f8fe0', // blue
  'c17fe0', // orchid
  'd98c4f', // terracotta
  '5ab8a0', // teal-green
  'b0a04a', // olive
  '8f7fe0', // indigo
  'e08f6f' // coral
];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

// Every agent owned by the same human gets the same color, regardless of
// which agent it is — that's the visual link back to "whose agent is this".
export function ownerColor(ownerId: string | null): string {
  if (!ownerId) return '9ca3af'; // neutral gray — owner deleted/unknown
  return OWNER_COLORS[hashString(ownerId) % OWNER_COLORS.length];
}

// Each agent's own robot "look" — seeded by the agent's id, so it's stable
// across reloads but different from every other agent. Deliberately NOT
// colored via DiceBear's own `backgroundColor` option — `bottts` gives each
// robot its own randomized accent colors (body/eye panels) regardless, which
// at a small icon size visually drowns out a DiceBear-level background
// color. The owner's color is applied instead as a CSS ring around the
// image (see the <span> wrapper at the call site) — small, reliable, and
// unaffected by whatever accent colors a given seed happens to roll.
export function agentAvatarDataUri(agentId: string): string {
  const avatar = createAvatar(bottts, { seed: agentId });
  return avatar.toDataUri();
}
